import {ReactElement, ReactNode, useCallback, useEffect} from "react";
import styled from "styled-components";

import {validateContactFournisseur} from "@/Api/notification";
import IconContact from "@/assets/iconContact.svg?react";
import IconCross from "@/assets/iconCross.svg?react";
import IconValide from "@/assets/iconValide.svg?react";
import {AppContext} from "@/context/AppContext";
import {breakpoints, quicksandBold, quicksandMedium} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {useModal} from "@/hooks/useModal/useModale";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Notification as NotificatonType} from "@/ts/fournisseur";

import {BlocContent} from "../BlocContent/BlocContent";
import {Button} from "../Button/Button";
import Loader from "../Loader/Loader";

const NotificationBlocContent = styled(BlocContent)<{
  $isContactNotification: boolean;
}>`
  width: calc(100% - 4rem);
  margin: 0;
  padding: 2rem 2rem;
  background-color: ${({$isContactNotification, theme}) =>
    $isContactNotification ? theme.colors.lightPrimary : "white"};
  container: notificationContainer / inline-size;
`;

const NotificationContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 30px;
  row-gap: 30px;
  @container notificationContainer (width<750px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;
const NotificationType = styled.p<{$isContactNotification: boolean}>`
  font-size: 1.5rem;
  text-align: center;
  ${quicksandBold}
  color: ${({$isContactNotification, theme}) =>
    $isContactNotification ? "white" : theme.colors.lightPrimary};
  min-width: 250px;
  @container notificationContainer (width<450px) {
    min-width: 0;
  }
`;

const AdherentInfo = styled.div<{$isContactNotification: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: none;
  min-width: 300px;
  max-width: 100%;
  @container notificationContainer (width<450px) {
    min-width: 0;
  }

  & p {
    color: ${({$isContactNotification, theme}) =>
      $isContactNotification ? "white" : theme.colors.darkPrimary};
    font-size: 1rem;
    ${quicksandBold}
  }
  & span {
    margin-left: 20px;
    color: ${({$isContactNotification, theme}) =>
      $isContactNotification ? "white" : theme.colors.lightPrimary};
    font-size: 0.875rem;
    ${quicksandMedium}
  }
`;

const InfoBloc = styled.div`
  border: 2px solid ${({theme}) => theme.colors.darkPrimary};
  grid-area: info;
  min-width: 300px;
  height: fit-content;
  max-width: calc(100% - 64px);
  padding: 15px 30px;
  width: 100%;
  justify-self: center;
  & h2 {
    color: ${({theme}) => theme.colors.darkPrimary};
    font-size: 1.125rem;
    text-align: center;
    ${quicksandBold}
  }
  @media (max-width: ${breakpoints.md}) {
    min-width: 210px;
    padding: 15px 15px;
    flex: none;
  }
`;
const LigneInfo = styled.p`
  color: ${({theme}) => theme.colors.darkPrimary};
  font-size: 1rem;
  ${quicksandBold}
  margin:15px 0;
  & span {
    color: ${({theme}) => theme.colors.lightPrimary};
    ${quicksandMedium}
  }
`;
const ButtonValidate = styled(Button)`
  margin: 15px auto;
  width: fit-content;
`;

export function Notification({
  notification,
  children,
  ...props
}: {
  notification: NotificatonType;
  children?: ReactNode;
  [key: string]: any;
}): ReactElement {
  const {ModalComponent, setModalData, modalData, closeModal} = useModal(
    () => {},
    null
  );
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  const isContactNotification = notification.type === "demande-contact";
  const validateContactLoadingState = useLoadingState();
  const buildModalContent = (): ReactNode => (
    <InfoBloc data-testid="InfoBloc">
      <h2>Cet adhérent souhaite être contacté</h2>
      <LigneInfo data-testid="LigneInfo-nom">
        Nom : <span>{notification?.adherent?.nom}</span>
      </LigneInfo>
      <LigneInfo data-testid="LigneInfo-adresse">
        Adresse : <span>{notification?.adherent?.adresse}</span>
      </LigneInfo>
      <LigneInfo data-testid="LigneInfo-mail">
        Mail : <span>{notification?.adherent?.email}</span>
      </LigneInfo>
      <ButtonValidate
        label="Valider la prise de contact"
        icon={
          validateContactLoadingState.isLoading ? (
            <Loader isIcon={true} color="white" />
          ) : validateContactLoadingState.hasError ? (
            IconCross
          ) : (
            IconValide
          )
        }
        disabled={validateContactLoadingState.isLoading}
        variant={
          validateContactLoadingState.isSuccess
            ? "validate"
            : validateContactLoadingState.hasError
              ? "negate"
              : "secondary"
        }
        onClick={async () => {
          if (validateContactLoadingState.isSuccess) {
            closeModal();
          }
          await validateContactLoadingState.executeWithLoading(async () => {
            await validateContactFournisseur(API, notification.notification_id);
          });
        }}
      />
    </InfoBloc>
  );

  const handleCloseModal = useCallback(async (): Promise<void> => {
    if (validateContactLoadingState.isSuccess) {
      await props?.refreshAllNotifications();
    }
  }, [validateContactLoadingState.isSuccess, props?.refreshAllNotifications]);
  // à l’ouverture
  const handleOpenModal = (): void => {
    setModalData({
      isOpen: true,
      content: buildModalContent(),
      close: handleCloseModal,
      theme: "secondary",
    });
  };

  // pour rafraîchir l’UI quand l’état change
  useEffect(() => {
    setModalData({
      ...modalData,
      content: buildModalContent(),
      close: handleCloseModal,
    });
  }, [
    validateContactLoadingState.isLoading,
    validateContactLoadingState.isSuccess,
    validateContactLoadingState.hasError,
  ]);
  return (
    <NotificationBlocContent $isContactNotification={isContactNotification}>
      <NotificationContent>
        <AdherentInfo $isContactNotification={isContactNotification}>
          <p>{`${notification?.adherent?.nom} (${notification?.adherent?.contremarque})`}</p>
          <span>{`${notification?.adherent?.adresse} `}</span>
        </AdherentInfo>

        <div>
          <NotificationType $isContactNotification={isContactNotification}>
            {notification.type}
          </NotificationType>
        </div>
        {isContactNotification ? (
          <Button
            label="Contacter"
            icon={IconContact}
            onClick={handleOpenModal}
          />
        ) : (
          children
        )}
      </NotificationContent>
      {ModalComponent}
    </NotificationBlocContent>
  );
}
